import dayjs from '@/lib/dayjs'
import { resolveEventImageUrl } from '@/lib/racing-images'
import apiFetch from '@/store/helpers/api-fetch'
import { timezoneDateTime } from './datetime'

export function getMainSession({ event }) {
  if (event.raceSession) {
    return event.raceSession
  }

  if (!event.sessions) return
  if (event.sessions.length === 1) return event.sessions[0]

  const raceSessions = event.sessions.filter(
    (session) => session.type === 'race',
  )

  const sortedSessions = (
    raceSessions.length > 0 ? raceSessions : event.sessions
  ).sort(
    (a, b) =>
      new Date(a.targetDateTime).getTime() -
      new Date(b.targetDateTime).getTime(),
  )

  return sortedSessions[sortedSessions.length - 1]
}

export function getFirstNonPracticeSession({ event }) {
  if (!event.sessions) return
  if (event.sessions.length === 1) return event.sessions[0]

  const raceSessions = event.sessions.filter(
    (session) => session.type !== 'practice',
  )

  const sortedSessions = (
    raceSessions.length > 0 ? raceSessions : event.sessions
  ).sort(
    (a, b) =>
      new Date(a.targetDateTime).getTime() -
      new Date(b.targetDateTime).getTime(),
  )

  return sortedSessions[0]
}

/**
 * Gets the first session from a set of events, checks for non practice sessions first
 * @param {Array<Event> } events - Array of events to check
 * @returns {<obj || null>} The first session from the events
 */
export function getFirstSession(events) {
  if (!events) return null

  try {
    const nonPracticeSessions = events
      .flatMap((event) => event.sessions)
      .filter((session) => session.type !== 'practice')
      .sort((a, b) => dayjs(a.targetDateTime).diff(dayjs(b.targetDateTime)))

    if (nonPracticeSessions.length > 0) {
      return nonPracticeSessions[0]
    }

    const practiceSessions = events
      .flatMap((event) => event.sessions)
      .filter((session) => session.type === 'practice')
      .sort((a, b) => dayjs(a.targetDateTime).diff(dayjs(b.targetDateTime)))

    if (practiceSessions.length > 0) {
      return practiceSessions[0]
    }
  } catch (e) {
    console.error(e)
    return null
  }

  return null
}

/**
 * Get the current or next session from a set of events
 * Uses the session start time and duration to accurately determine what events have ended and returns the next occuring session
 * @param {Array<Event> } events - Array of events to check
 * @returns {<obj || null>} The current or next session from the events
 */
export function getCurrentOrNextSession(events) {
  if (!events) return null

  try {
    const nonPracticeNonFinishedSessions = events
      .flatMap((event) => event.sessions)
      .filter((session) => session.type !== 'practice')
      .filter((session) =>
        timezoneDateTime({
          datetime: session.finishDateTime,
          timezone: session.targetTimezone,
          format: false,
        }).isAfter(dayjs()),
      )
      .sort((a, b) => dayjs(a.targetDateTime).diff(dayjs(b.targetDateTime)))

    if (nonPracticeNonFinishedSessions.length > 0) {
      return nonPracticeNonFinishedSessions[0]
    }

    const practiceNonFinishedSessions = events
      .flatMap((event) => event.sessions)
      .filter((session) => session.type === 'practice')
      .filter((session) =>
        timezoneDateTime({
          datetime: session.finishDateTime,
          timezone: session.targetTimezone,
          format: false,
        }).isAfter(dayjs()),
      )
      .sort((a, b) => dayjs(a.targetDateTime).diff(dayjs(b.targetDateTime)))

    if (practiceNonFinishedSessions.length > 0) {
      return practiceNonFinishedSessions[0]
    }
  } catch (e) {
    console.error(e)
    return null
  }

  return null
}

async function hasEnoughDrivers(event) {
  const eventStats = await apiFetch({
    url: `community/${event.communityId}/league/${event.leagueId}/event/${event.id}/stats`,
  })

  return eventStats?.attendees.drivers / (event.gridPositions || 1) > 0.5
}

export const eventTags = [
  // {
  //   label: 'Action required',
  //   bg: 'bg-error',
  //   color: 'white',
  //   border: 'border-error',
  //   condition: (event) => {
  //     return !hasEnoughDrivers(event) && !event.isPast
  //   },
  // },
  {
    label: 'Assign points',
    bg: 'bg-gf-gradient',
    color: 'white',
    border: 'border-primary',
    condition: (event) => {
      return event.isPast && !event.resultsPublished
    },
  },
  {
    label: 'Ready to Race',
    bg: 'bg-success',
    color: 'green-800',
    border: 'border-success',
    condition: (event) => async () => {
      return !event.isPast && (await hasEnoughDrivers(event))
    },
  },
  {
    label: 'Upcoming',
    bg: 'bg-blue-200',
    color: 'dark',
    condition: (event) => {
      return !event.isPast && event.active
    },
  },
  {
    label: 'Finished',
    bg: 'bg-shades-0',
    color: 'dark',
    condition: (event) => {
      const mainSession = getMainSession({ event: event })
      const isFinished =
        mainSession?.type === 'hotlap'
          ? dayjs(mainSession.targetDateTime)
              .tz(mainSession.targetTimezone)
              .add(mainSession.duration, 'day')
              .isBefore()
          : event.isPast

      return event.isPast && isFinished && event.active
    },
  },
  {
    label: 'Draft',
    bg: 'bg-neutral-blue-200',
    color: 'dark',
    condition: (event) => {
      return !event.active
    },
  },
]

export function getEventTags(event) {
  let tags = []
  eventTags.forEach((tag) => {
    if (event && tag.condition(event)) {
      tags.push(tag)
    }
  })
  return tags
}

/**
 * Formats a number of minutes into a human readable duration string
 * @param {number} minutes
 * @returns {string} Formatted duration string
 */
export function formatSessionDuration(minutes) {
  const days = Math.floor(minutes / (24 * 60))
  const hours = Math.floor((minutes % (24 * 60)) / 60)
  const remainingMinutes = minutes % 60

  if (days > 0) {
    return `${days}d ${hours}h ${remainingMinutes}m`
  } else if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`
  } else {
    return `${remainingMinutes}m`
  }
}

/**
 * Calculates the total duration of an event
 * @param {object} event
 * @returns {number} Total duration of the event in minutes
 */
export function getTotalEventDuration(event) {
  let tempEventDuration = 0

  for (const session of event.sessions) {
    const toNumber = Number(session.duration)

    if (isNaN(toNumber)) continue

    switch (session.durationType) {
      case 'min':
        tempEventDuration += Number(session.duration)
        break
      case 'hour':
        tempEventDuration += Number(session.duration) * 60
        break
      case 'lap':
        tempEventDuration += Number(session.duration) * 10
        break
      case 'day':
        tempEventDuration += Number(session.duration) * 60 * 24
        break
    }
  }

  return tempEventDuration
}

/**
 * Returns the shared event image priority used by public pages and email.
 * @param {object} event
 * @returns {string} Image URL
 */
export function getEventImage(event) {
  return resolveEventImageUrl(event)
}
